/**
 * TradingView Charts Components
 * Version: 6.2.0 (Fixed CSS variable color handling)
 * 
 * Performance charts using TradingView Widgets
 */
import { useEffect, useRef, useState } from 'react'
import './TradingViewCharts.css'
import TradeOverlay from './ManualTradeOverlay'

// Helper to convert CSS variable to actual color
function resolveColor(color: string): string {
  if (color.startsWith('var(--')) {
    const varName = color.slice(4, -1)
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
    return value || '#6366f1'
  }
  return color
}

// Helper to add alpha to a color
function addAlpha(color: string, alpha: string): string {
  const resolved = resolveColor(color)
  if (resolved.startsWith('rgba') || resolved.startsWith('rgb')) {
    return resolved
  }
  if (resolved.startsWith('#')) {
    let hex = resolved.slice(1)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('')
    }
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${parseInt(alpha, 16) / 255})`
  }
  return resolved
}

// TradingView Widget Script Loader
const loadTradingViewScript = (): Promise<void> => {
  return new Promise((resolve) => {
    if (document.getElementById('tradingview-widget-script')) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = 'tradingview-widget-script'
    script.src = 'https://s3.tradingview.com/tv.js'
    script.async = true
    script.onload = () => resolve()
    document.head.appendChild(script)
  })
}

// ============================================
// Equity Chart Component
// ============================================
interface EquityChartProps {
  symbol?: string
  height?: number
  enableManualTrade?: boolean
  currentPrice?: number
  exchange?: string
}

function EquityChart({ 
  symbol = 'NIFTY', 
  height = 400,
  enableManualTrade = true,
  currentPrice = 0,
  exchange = 'binance'
}: EquityChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let widget: any = null

    const initWidget = async () => {
      await loadTradingViewScript()

      if (!containerRef.current) return

      setIsLoading(false)

      widget = new (window as any).TradingView.widget({
        symbol: symbol,
        interval: 'D',
        timezone: 'Asia/Kolkata',
        theme: 'dark',
        style: '1',
        locale: 'en',
        toolbar_bg: '#1a1a2e',
        enable_publishing: false,
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: false,
        container_id: containerRef.current.id,
        autosize: true,
        height: height,
        studies: ['MASimple@tv-basicstudies'],
        overrides: {
          'mainSeriesProperties.candleStyle.upColor': '#10b981',
          'mainSeriesProperties.candleStyle.downColor': '#ef4444',
          'mainSeriesProperties.candleStyle.borderUpColor': '#10b981',
          'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
          'mainSeriesProperties.candleStyle.wickUpColor': '#10b981',
          'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444',
        }
      })
      
      // Listen for price updates
      widget.onChartReady(() => {
        widget.createStudy('MASimple', false, false, [14], (result: any) => {
          console.log('Chart ready with symbol:', symbol)
        })
      })
    }

    initWidget()

    return () => {
      if (widget?.remove) {
        widget.remove()
      }
    }
  }, [symbol, height])

  return (
    <div className="tradingview-chart equity-chart" style={{ position: 'relative' }}>
      {isLoading && (
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <span>Loading chart...</span>
        </div>
      )}
      <div 
        id={`equity-chart-${symbol}`} 
        ref={containerRef} 
        className="chart-container"
      />
      {enableManualTrade && !isLoading && (
        <TradeOverlay
          symbol={symbol}
          exchange={exchange}
          currentPrice={currentPrice}
          position="top-right"
        />
      )}
    </div>
  )
}

// ============================================
// P&L Line Chart Component
// ============================================
interface LineChartProps {
  data?: { time: string; value: number }[]
  color?: string
  height?: number
  showGrid?: boolean
  showArea?: boolean
}

function LineChart({ 
  data = [], 
  color = '#6366f1', 
  height = 300,
  showGrid = true,
  showArea = true
}: LineChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const rect = container.getBoundingClientRect()
    canvas.width = rect.width * window.devicePixelRatio
    canvas.height = height * window.devicePixelRatio
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${height}px`
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio)

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, height)

    if (data.length === 0) {
      // Draw placeholder
      ctx.fillStyle = '#888'
      ctx.font = '14px Inter, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('No data available', rect.width / 2, height / 2)
      return
    }

    // Calculate bounds
    const padding = { top: 20, right: 60, bottom: 30, left: 20 }
    const chartWidth = rect.width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    const values = data.map(d => d.value)
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    const valueRange = maxValue - minValue || 1

    // Scale functions
    const xScale = (i: number) => padding.left + (i / (data.length - 1 || 1)) * chartWidth
    const yScale = (v: number) => padding.top + chartHeight - ((v - minValue) / valueRange) * chartHeight

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.lineWidth = 1
      
      // Horizontal lines
      for (let i = 0; i <= 4; i++) {
        const y = padding.top + (i / 4) * chartHeight
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(rect.width - padding.right, y)
        ctx.stroke()
      }
    }

    // Draw area gradient
    if (showArea) {
      const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom)
      gradient.addColorStop(0, addAlpha(color, '40'))
      gradient.addColorStop(1, addAlpha(color, '00'))

      ctx.beginPath()
      ctx.moveTo(xScale(0), height - padding.bottom)
      data.forEach((d, i) => {
        ctx.lineTo(xScale(i), yScale(d.value))
      })
      ctx.lineTo(xScale(data.length - 1), height - padding.bottom)
      ctx.closePath()
      ctx.fillStyle = gradient
      ctx.fill()
    }

    // Draw line
    ctx.beginPath()
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'

    data.forEach((d, i) => {
      if (i === 0) {
        ctx.moveTo(xScale(i), yScale(d.value))
      } else {
        ctx.lineTo(xScale(i), yScale(d.value))
      }
    })
    ctx.stroke()

    // Draw data points
    data.forEach((d, i) => {
      ctx.beginPath()
      ctx.arc(xScale(i), yScale(d.value), 4, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.strokeStyle = '#1a1a2e'
      ctx.lineWidth = 2
      ctx.stroke()
    })

    // Draw Y-axis labels
    ctx.fillStyle = '#888'
    ctx.font = '11px Inter, sans-serif'
    ctx.textAlign = 'right'

    const formatValue = (v: number) => {
      if (Math.abs(v) >= 100000) return `${(v / 100000).toFixed(1)}L`
      if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`
      return v.toFixed(0)
    }

    ctx.fillText(formatValue(maxValue), rect.width - 10, padding.top + 5)
    ctx.fillText(formatValue(minValue), rect.width - 10, height - padding.bottom + 5)
    ctx.fillText(formatValue((maxValue + minValue) / 2), rect.width - 10, padding.top + chartHeight / 2 + 5)

  }, [data, color, height, showGrid, showArea])

  return (
    <div ref={containerRef} className="line-chart-container">
      <canvas ref={canvasRef} />
    </div>
  )
}

// ============================================
// Bar Chart Component
// ============================================
interface BarChartData {
  label: string
  value: number
  color?: string
}

interface BarChartProps {
  data: BarChartData[]
  height?: number
  showValues?: boolean
}

function BarChart({ data = [], height = 250, showValues = true }: BarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = container.getBoundingClientRect()
    canvas.width = rect.width * window.devicePixelRatio
    canvas.height = height * window.devicePixelRatio
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${height}px`
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio)

    const padding = { top: 20, right: 20, bottom: 40, left: 20 }
    const chartWidth = rect.width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    const maxValue = Math.max(...data.map(d => Math.abs(d.value)))
    const barWidth = Math.min(40, (chartWidth / data.length) - 10)
    const barGap = (chartWidth - barWidth * data.length) / (data.length + 1)

    data.forEach((d, i) => {
      const x = padding.left + barGap + i * (barWidth + barGap)
      const barHeight = (Math.abs(d.value) / maxValue) * chartHeight
      const y = d.value >= 0 
        ? padding.top + chartHeight - barHeight 
        : padding.top + chartHeight

      // Draw bar
      ctx.fillStyle = d.color || (d.value >= 0 ? '#10b981' : '#ef4444')
      ctx.beginPath()
      ctx.roundRect(x, y, barWidth, barHeight, [4, 4, 0, 0])
      ctx.fill()

      // Draw label
      ctx.fillStyle = '#888'
      ctx.font = '11px Inter, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(d.label, x + barWidth / 2, height - 15)

      // Draw value
      if (showValues) {
        ctx.fillStyle = d.value >= 0 ? '#10b981' : '#ef4444'
        ctx.font = '10px Inter, sans-serif'
        const valueText = d.value >= 0 ? `+${d.value}` : `${d.value}`
        ctx.fillText(valueText, x + barWidth / 2, d.value >= 0 ? y - 5 : y + barHeight + 15)
      }
    })
  }, [data, height, showValues])

  return (
    <div ref={containerRef} className="bar-chart-container">
      <canvas ref={canvasRef} />
    </div>
  )
}

// ============================================
// Candlestick Chart Component
// ============================================
interface CandleData {
  time: string
  open: number
  high: number
  low: number
  close: number
}

interface CandlestickChartProps {
  data?: CandleData[]
  height?: number
}

function CandlestickChart({ data = [], height = 350 }: CandlestickChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = container.getBoundingClientRect()
    canvas.width = rect.width * window.devicePixelRatio
    canvas.height = height * window.devicePixelRatio
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${height}px`
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio)

    const padding = { top: 10, right: 60, bottom: 30, left: 10 }
    const chartWidth = rect.width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    if (data.length === 0) {
      ctx.fillStyle = '#888'
      ctx.font = '14px Inter, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('No candle data', rect.width / 2, height / 2)
      return
    }

    const values = data.flatMap(d => [d.high, d.low])
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    const valueRange = maxValue - minValue || 1

    const yScale = (v: number) => padding.top + chartHeight - ((v - minValue) / valueRange) * chartHeight

    const candleWidth = Math.max(3, Math.min(12, (chartWidth / data.length) - 4))
    const candleGap = (chartWidth - candleWidth * data.length) / (data.length + 1)

    data.forEach((candle, i) => {
      const x = padding.left + candleGap + i * (candleWidth + candleGap)
      const isGreen = candle.close >= candle.open
      const color = isGreen ? '#10b981' : '#ef4444'

      // Wick
      ctx.strokeStyle = color
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x + candleWidth / 2, yScale(candle.high))
      ctx.lineTo(x + candleWidth / 2, yScale(candle.low))
      ctx.stroke()

      // Body
      const bodyTop = yScale(Math.max(candle.open, candle.close))
      const bodyBottom = yScale(Math.min(candle.open, candle.close))
      const bodyHeight = Math.max(1, bodyBottom - bodyTop)

      ctx.fillStyle = color
      ctx.beginPath()
      ctx.roundRect(x, bodyTop, candleWidth, bodyHeight, [2, 2, 2, 2])
      ctx.fill()
    })

    // Y-axis labels
    ctx.fillStyle = '#888'
    ctx.font = '10px Inter, sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText(maxValue.toFixed(0), rect.width - 5, padding.top + 12)
    ctx.fillText(minValue.toFixed(0), rect.width - 5, height - padding.bottom)
  }, [data, height])

  return (
    <div ref={containerRef} className="candlestick-chart-container">
      <canvas ref={canvasRef} />
    </div>
  )
}

// ============================================
// Export
// ============================================
export { 
  EquityChart, 
  LineChart, 
  BarChart, 
  CandlestickChart 
}

export type { EquityChartProps, LineChartProps, BarChartProps, BarChartData, CandleData }